import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Archive } from "lucide-react";
import { api } from "../lib/api";

/**
 * Storage / Retention banner surfaced on the Dashboard.
 * Only renders when the user is at day-21 or day-28 idle. Records the
 * impression via /account/retention/reminder-shown so the same tier is not
 * re-shown unnecessarily (fires once per tier per user).
 */
export default function RetentionBanner({ retention }) {
  const tier = retention?.reminder_tier;
  const dismissKey = tier ? `prose_retention_dismissed_${tier}` : null;
  const [dismissed, setDismissed] = useState(() => {
    if (!dismissKey) return false;
    try { return sessionStorage.getItem(dismissKey) === "1"; } catch { return false; }
  });

  useEffect(() => {
    const tier = retention?.reminder_tier;
    if (!tier) return;
    // Suppress client-side re-posts within this browser session; the backend
    // is idempotent per tier as well, so worst-case we'd get one no-op call
    // per full page refresh even without this guard.
    const key = `prose_retention_seen_${tier}`;
    if (sessionStorage.getItem(key)) return;
    api
      .post("/account/retention/reminder-shown", { tier })
      .then(() => { try { sessionStorage.setItem(key, "1"); } catch {} })
      .catch(() => {});
  }, [retention?.reminder_tier]);

  if (!tier || dismissed) return null;

  const isFinal = tier >= 28;
  const days = retention.days_until_removal;
  const idle = retention.idle_days;

  const onDismiss = () => {
    setDismissed(true);
    if (dismissKey) { try { sessionStorage.setItem(dismissKey, "1"); } catch {} }
  };

  return (
    <div
      className={`mb-6 border px-5 py-4 text-sm flex items-start gap-3 ${
        isFinal
          ? "border-red-300 bg-red-50 text-red-900"
          : "border-amber-300 bg-amber-50 text-amber-900"
      }`}
      data-testid={`retention-banner-day-${tier}`}
    >
      {isFinal ? (
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-red-700" />
      ) : (
        <Archive className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
      )}
      <div className="flex-1 leading-relaxed">
        <div className="font-medium">
          {isFinal
            ? `Final reminder — your workspace is scheduled for removal in ${days} day${days === 1 ? "" : "s"}.`
            : `Your workspace has been idle for ${idle} days.`}
        </div>
        <div className={`mt-1 ${isFinal ? "text-red-800" : "text-amber-800"}`}>
          Free workspaces during the R&amp;D phase include up to {retention.storage_quota_mb} MB of storage
          and may be removed after {retention.inactivity_limit_days} days of inactivity. You can{" "}
          <Link to="/account" className="underline underline-offset-4 font-medium">
            download your materials or extend your workspace
          </Link>{" "}
          at any time.
        </div>
      </div>
      <button
        onClick={onDismiss}
        className={`shrink-0 text-xs font-mono-tech uppercase tracking-wider px-3 py-1.5 border ${
          isFinal
            ? "border-red-300 text-red-900 hover:bg-red-100"
            : "border-amber-300 text-amber-900 hover:bg-amber-100"
        }`}
        data-testid="retention-banner-dismiss"
        aria-label="Dismiss"
      >
        Dismiss
      </button>
    </div>
  );
}
